<template lang="pug">
v-container(fluid )
  AddCreditDialog(v-if="showDialog" :show="showDialog" @close="closeDialog" :boarder="boarder" @saved="savedCreditData" @error="showErrorSnackbar")
  RemoveCreditDialog(v-if="showRemoveDialog" :showRemoveDialog="showRemoveDialog" @close="closeRemoveDialog" :boarder="boarder" @saved="savedRemoveCreditData" @error="errorSnackbar")
  v-snackbar(v-model="showErrorSnackbar" color="error")
    span {{ $t('errors.valueNotSaved') }}
  v-snackbar( v-model="snackBar" :color="snackBarColor" ) {{ snackBarMessage }}
    template( v-slot:action="{attrs}")
      v-btn(text v-bind="attrs"
        @click="snackBar = false") {{ $t('buttons.closeButtonLabel') }}
  v-row.justify-start.ma-0(no-gutters v-if="$vuetify.breakpoint.mdAndUp")
    v-col.text-end.pl-0(cols="5")
      v-btn(@click="onAddCredit" color="success" small)
        v-icon.mr-3(small left) fas fa-plus
        span {{ $t('addCreditValue') }}
    v-col.pa-0(cols="1")
    v-col.text-start.pl-0(cols="4")
      v-btn(@click="onRemoveCredit" color="error" small)
        v-icon.mr-3(small left) fas fa-minus
        span {{ $t('removeCreditValue') }}
  v-row.justify-start.ma-0(no-gutters v-else)
    v-col.pa-0(cols="12")
      v-btn(@click="onAddCredit" color="success")
        v-icon.mr-3(small left) fas fa-plus
        span {{ $t('addCreditValue') }}
    v-col.pa-0.pt-2(cols="12")
      v-btn(@click="onRemoveCredit" color="error")
        v-icon.mr-3(small left) fas fa-minus
        span {{ $t('removeCreditValue') }}

  v-tabs
    v-tab {{ $t('boarderDetailDialog.creditBalanceTabTitle') }}
    v-tab {{ $t('boarderDetailDialog.creditMovementsTabTitle') }}

    v-tab-item
      v-row(v-if="!fetchInProgress").justify-center.mt-10.mb-7
        div.pr-1.font-weight-bold.text-h3(:class="getColor") {{formattedCurrentCreditBalance}}
        v-icon.font-weight-bold.ml-2(x-large :class="getColor") fal fa-euro-sign

      v-skeleton-loader(v-else type="paragraph@1 " width="100%").mt-10

    v-tab-item
      v-row.ma-0(style="height: 390px; overflow-y: scroll" v-if="!fetchInProgress" )
        v-col( cols="12")
          v-list
            v-list-item(v-for="(item, idx) in creditDetails" :key="idx")
              v-list-item-content.align-self-start
                v-row.ma-0(no-gutters)
                  v-col(cols="4")
                    v-list-item-title
                      div.font-weight-bold.title {{formatDate(item.createdAt)}}
                  v-col(cols="8")
                    v-row.ma-0.align-center(no-gutters)
                      v-col(cols="9" )
                        v-row.ma-0
                          v-spacer
                          div.font-weight-bold.title(:class="getColor(item.amount)") {{getAmount(item.amount)}}
                          v-icon.font-weight-bold.ml-1(:class="getColor(item.amount)" size="18") fas fa-euro-sign
              v-list-item-content {{item.description}}
      v-skeleton-loader(v-else type="paragraph@7 " width="100%")

</template>

<script lang="ts">
import Vue from 'vue';
import {Component, Prop} from "vue-property-decorator";
import {FETCH_BOARDER_DETAIL, FETCH_CREDIT_HISTORY} from "@/store/action-types";
import {Action} from "vuex-class";
import CreditDetails from "@/model/CreditDetails";
import type {BoarderDetailResponse, BoarderResponse} from "@/types/service-response";
import moment from "moment/moment";
import AddCreditDialog from "@/components/boarder-management/AddCreditDialog.vue";
import RemoveCreditDialog from "@/components/boarder-management/RemoveCreditDialog.vue";
import {formatFloatPrice} from "@/services/formatter";
import {FetchBoarderDetailPayload} from "@/types/payload";

@Component({
  components: {
    AddCreditDialog,
    RemoveCreditDialog,
  }
})
export default class CreditTab extends Vue {
  @Action(FETCH_CREDIT_HISTORY) fetchCreditHistory!: ({ (payload: number): Promise<CreditDetails[]> })
  @Action(FETCH_BOARDER_DETAIL) fetchBoarderDetails!: ({ (payload: FetchBoarderDetailPayload): Promise<BoarderDetailResponse> })
  @Prop({type: Object, required: false}) readonly boarder!: BoarderResponse

  private creditDetails: CreditDetails[] | null = null
  private showDialog: boolean = false
  private showRemoveDialog: boolean = false
  private showErrorSnackbar: boolean = false
  private fetchInProgress: boolean = false

  private snackBar: boolean = false
  private snackBarColor: string = ''
  private snackBarMessage: string = ''

  private currentCreditBalance: number | null = null
  private formattedCurrentCreditBalance: string = ''

  onRemoveCredit(){
    this.showRemoveDialog = true
  }

  onAddCredit(){
    this.showDialog = true
  }

  formatDate(date: string): string {
    return moment(date).format('DD.MM.YYYY')
  }

  getColor(value:number): string {
    if(value < 0){
      return 'red--text'
    }else if(value > 0){
      return 'green--text'
    }else{
      return ''
    }
  }

  getAmount(item: number) {
    return item.toFixed(2)
  }

  closeDialog (): void {
    this.showDialog = false
  }

  closeRemoveDialog (): void {
    this.showRemoveDialog = false
  }

  errorSnackbar (): void {
    this.showErrorSnackbar = true
  }

  savedCreditData(): void {
    this.$emit('saved')

    this.snackBar = true
    this.snackBarMessage = this.$t('valueSaved').toString()
    this.snackBarColor = 'success'

    this.closeDialog()
    this.fetchCredit(this.boarder.person.id)
  }

  savedRemoveCreditData(): void {
    this.$emit('saved')

    this.snackBar = true
    this.snackBarMessage = this.$t('valueSaved').toString()
    this.snackBarColor = 'success'

    this.closeRemoveDialog()
    this.fetchCredit(this.boarder.person.id)
  }

  async fetchCredit(payload: number): Promise<void>{
    this.fetchInProgress = true

    try {
      this.creditDetails = await this.fetchCreditHistory(payload)

      const boarderDetailPayload: FetchBoarderDetailPayload = {
        id: payload
      }
      const response: BoarderDetailResponse = await this.fetchBoarderDetails(boarderDetailPayload)
      this.currentCreditBalance = response.wallets[0].currentBalance
      this.formattedCurrentCreditBalance = formatFloatPrice(this.currentCreditBalance)
    } catch (e) {
      this.snackBar = true
      this.snackBarColor = 'alert'
      this.snackBarMessage = this.$t('errors.fetchDataError').toString()
    } finally {
      this.fetchInProgress = false
    }
  }

  mounted() {
    this.fetchCredit(this.boarder.person.id)
  }
}

</script>

<style lang="stylus" scoped>
.ellipses
  overflow hidden
  text-overflow ellipsis
</style>
